import { createIdempotencyKey } from '../api';
import { createSignaturePad } from '../components/signature';
import { mountTurnstile, type TurnstileControl } from '../turnstile';

type OutletOption = {
  code: string;
  name: string;
  address: string;
  fullTimeHours: string;
  partTimeHours: string;
  positions: string[];
};

const OUTLETS: OutletOption[] = [
  {
    code: 'KCH',
    name: 'Royal Richmond Square',
    address: '15 Ground Floor, Jalan Bandar Musthapha, Royal Richmond Square, 93250 Kuching, Sarawak',
    fullTimeHours: 'Royal Richmond Square full-time roster. Confirm exact weekly days and hours with HR before publishing.',
    partTimeHours: 'Royal Richmond Square part-time roster. Applicant should state available days and time blocks below.',
    positions: ['Crew', 'Cashier', 'Kitchen Crew', 'Service Crew'],
  },
  {
    code: 'JDBM',
    name: 'Jalan Datuk Bandar Mustapha',
    address: 'Jalan Datuk Bandar Mustapha, Kuching, Sarawak',
    fullTimeHours: 'Jalan Datuk Bandar Mustapha full-time roster. Confirm exact weekly days and hours with HR before publishing.',
    partTimeHours: 'Jalan Datuk Bandar Mustapha part-time roster. Applicant should state available days and time blocks below.',
    positions: ['Crew', 'Cashier', 'Kitchen Crew', 'Service Crew'],
  },
];

const DEFAULT_OUTLET = OUTLETS[0] as OutletOption;

const WORK_LABELS: [string, string, string][] = [
  ['employer', 'Employer', 'text'],
  ['telephone', 'Telephone', 'tel'],
  ['from', 'Employment From', 'date'],
  ['to', 'Employment To', 'date'],
  ['position', 'Position / Job Title', 'text'],
  ['pay', 'Pay', 'text'],
  ['reason', 'Reason for Leaving', 'textarea'],
  ['mayContact', 'May We Contact', 'select'],
];

const EDUCATION_TYPES = ['High School', 'College / University', 'Trade School', 'Other'];

export function renderApplyPage(root: HTMLElement): void {
  const outletParam = (new URLSearchParams(location.search).get('outlet') || '').toUpperCase();
  const lockedOutlet = OUTLETS.find((outlet) => outlet.code === outletParam);
  const visibleOutlets = lockedOutlet ? [lockedOutlet] : OUTLETS;
  const formScope = lockedOutlet ? `${lockedOutlet.code} - ${lockedOutlet.name}` : 'All Outlets';

  root.innerHTML = `
    <main class="page application-page">
      <form class="paper application-paper" id="applyForm">
        <div class="masthead">
          <div><div class="brand-cn">阿笨的猪堡店</div><div class="brand-en">STUPIAK'S PORK BURGER</div></div>
          <div class="title-lockup"><div class="form-title">Job Application</div><div class="form-subtitle">${safe(formScope)}</div></div>
        </div>

        <div class="section-title">Location / Outlet</div>
        <div class="location-picker" id="locationPicker">
          ${visibleOutlets.map((outlet, index) => `
            <label class="location-card ${index === 0 ? 'selected' : ''}" data-outlet-card="${safeAttr(outlet.code)}">
              <input type="radio" name="outletCode" value="${safeAttr(outlet.code)}" ${index === 0 ? 'checked' : ''} required>
              <span><b>${safe(outlet.name)}</b><small>${safe(outlet.address)}</small></span>
            </label>`).join('')}
        </div>
        <input type="hidden" name="outletName" id="outletName">
        <input type="hidden" name="locationWorkTime" id="locationWorkTime">
        <div class="location-summary" id="locationSummary"></div>

        <div class="section-title">Personal Information</div>
        <div class="grid personal">
          <div class="cell span-8"><label>Name *</label><input name="name" autocomplete="name" required></div>
          <div class="cell span-4"><label>Email (Optional)</label><input name="email" type="email" autocomplete="email"></div>
          <div class="cell span-6"><label>IC Number *</label><input name="icNumber" required></div>
          <div class="cell span-3"><label>Home Phone</label><input name="homePhone" inputmode="tel"></div>
          <div class="cell span-3"><label>Mobile / WhatsApp *</label><input name="mobile" inputmode="tel" autocomplete="tel" required></div>
          <div class="cell span-6"><label>Street Address / Alamat *</label><input name="streetAddress" autocomplete="street-address" required></div>
          <div class="cell span-3"><label>City *</label><input name="city" required></div>
          <div class="cell span-3"><label>Zip / Postcode *</label><input name="zip" inputmode="numeric" required></div>
          <div class="cell span-6"><label>What position are you applying for? *</label><select name="position" id="position" required></select></div>
          <div class="cell span-6"><label>Applying for Full Time / Part Time *</label><select name="employmentType" id="employmentType" required><option value="">Select</option><option>Full-Time</option><option>Part-Time</option></select></div>
          <div class="cell span-4" id="hourlyCell"><label>Expected Hourly Rate (Part Time)</label><input name="expectedHourlyRate" inputmode="decimal" placeholder="RM"></div>
          <div class="cell span-4" id="salaryCell"><label>Expected Salary (Full Time)</label><input name="expectedSalary" inputmode="decimal" placeholder="RM"></div>
          <div class="cell span-4"><label>Date Available *</label><input name="dateAvailable" type="date" required></div>
          <div class="cell span-12"><label>Preferred Working Time / Availability *</label><textarea name="preferredWorkTime" required placeholder="Example: Mon-Fri after 6pm, Saturday full day, Sunday unavailable"></textarea></div>
          <div class="cell span-12"><label>Emergency Contact (Name & Phone) *</label><input name="emergencyContact" required></div>
          <div class="cell span-12"><label>CV (PDF, DOC or DOCX; maximum 8 MB)</label><input type="file" name="cv" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"></div>
        </div>

        <div class="section-title">Prior Work Experience</div>
        <div class="desktop-work">
          <table class="work-table">
            <thead><tr><th></th><th>Current or Most Recent</th><th>Prior</th><th>Prior</th></tr></thead>
            <tbody>${workTableRows()}</tbody>
          </table>
        </div>
        <div class="mobile-work">${mobileWorkCards()}</div>

        <div class="section-title">Education</div>
        <table class="education-table">
          <thead><tr><th></th><th>Name / Location</th><th>Last Year Complete</th><th>Degree</th><th>Major</th></tr></thead>
          <tbody>${educationRows()}</tbody>
        </table>

        <div class="section-title">List any applicable special skills, training or proficiencies.</div>
        <div class="skills-box"><textarea name="skills" placeholder="Food handling, cashier, kitchen, service, languages, certificates, etc."></textarea></div>

        <div class="section-title">Declaration & Signature</div>
        <label class="declaration-check"><input name="declarationAccepted" type="checkbox" value="true" required><span>By signing, I certify that the information above is correct. I understand that false information may prevent me from being hired or may lead to dismissal if hired. I consent to former employers being contacted regarding work records.</span></label>
        <div class="declaration-grid">
          <div class="declaration-text">The information supplied in this application will be used only for recruitment and employment-related purposes.</div>
          <div class="signature-wrap"><canvas id="signatureCanvas"></canvas><div class="sig-tools"><span>Sign with finger, mouse or trackpad.</span><button id="clearSignature" type="button">Clear</button></div></div>
          <div><label class="field-label">Date *</label><input name="declarationDate" id="declarationDate" type="date" required></div>
        </div>

        <input class="honeypot" name="website" tabindex="-1" autocomplete="off">
        <div id="turnstileWidget" class="turnstile"></div>
        <div class="privacy">Shortlisted applicants will be contacted through WhatsApp. This form does not automatically send email.</div>
        <div id="result" class="result"></div>
        <div class="actions"><button class="submit" id="submitButton" type="submit">Submit Application</button></div>
      </form>
    </main>`;

  const form = root.querySelector<HTMLFormElement>('#applyForm')!;
  const canvas = root.querySelector<HTMLCanvasElement>('#signatureCanvas')!;
  const pad = createSignaturePad(canvas);
  let turnstile: TurnstileControl = { getToken: () => '', reset: () => undefined };
  mountTurnstile(root.querySelector<HTMLElement>('#turnstileWidget')!, 'application_submit')
    .then((control) => { turnstile = control; })
    .catch((error) => showResult(root, 'error', error instanceof Error ? error.message : String(error)));

  root.querySelector('#clearSignature')?.addEventListener('click', pad.clear);
  root.querySelector<HTMLInputElement>('#declarationDate')!.value = today();
  root.querySelectorAll<HTMLInputElement>('input[name="outletCode"]').forEach((input) => {
    input.addEventListener('change', () => refreshOutlet(root));
  });
  root.querySelector<HTMLSelectElement>('#employmentType')?.addEventListener('change', () => refreshOutlet(root));
  syncMobileWork(root);
  refreshOutlet(root);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = root.querySelector<HTMLButtonElement>('#submitButton')!;
    const cv = form.elements.namedItem('cv') as HTMLInputElement;
    if (!pad.hasInk()) {
      showResult(root, 'error', 'Please sign inside the signature box.');
      return;
    }
    if (cv.files?.[0] && cv.files[0].size > 8 * 1024 * 1024) {
      showResult(root, 'error', 'CV must be 8 MB or smaller.');
      return;
    }

    const data = new FormData(form);
    data.set('signatureDataUrl', pad.toDataUrl());
    data.set('turnstileToken', turnstile.getToken());
    data.set('idempotencyKey', createIdempotencyKey('application'));
    data.set('workExperiencesJson', JSON.stringify(collectWork(root)));
    data.set('educationJson', JSON.stringify(collectEducation(root)));

    button.disabled = true;
    button.textContent = 'Submitting...';
    clearResult(root);

    try {
      const response = await fetch('/api/application', { method: 'POST', body: data });
      const body = await response.json() as { ok: boolean; data?: { applicationId: string }; error?: { message: string } };
      if (!response.ok || !body.ok) throw new Error(body.error?.message || 'Submission failed.');
      showResult(root, 'success', `<b>Application submitted successfully.</b><br>Application ID: ${safe(body.data?.applicationId || '-')}`);
      button.textContent = 'Submitted';
      Array.from(form.elements).forEach((element) => ((element as HTMLInputElement).disabled = true));
    } catch (error) {
      showResult(root, 'error', safe(error instanceof Error ? error.message : String(error)));
      turnstile.reset();
      button.disabled = false;
      button.textContent = 'Submit Application';
    }
  });
}

function workTableRows(): string {
  return WORK_LABELS.map(([key, label, type]) => `
    <tr><td class="row-label">${safe(label)}</td>
    ${[0, 1, 2].map((index) => `<td>${fieldHtml(`work${index}_${key}`, `work_${index}_${key}`, type)}</td>`).join('')}
    </tr>`).join('');
}

function mobileWorkCards(): string {
  return [0, 1, 2].map((index) => `
    <div class="work-card"><h3>${index === 0 ? 'Current or Most Recent' : `Prior ${index}`}</h3>
      ${WORK_LABELS.map(([key, label, type]) => `<div class="mobile-field"><label class="field-label">${safe(label)}</label>${fieldHtml(`mwork${index}_${key}`, `mwork_${index}_${key}`, type)}</div>`).join('')}
    </div>`).join('');
}

function educationRows(): string {
  return EDUCATION_TYPES.map((type, index) => `
    <tr><td class="row-label">${safe(type)}</td>
      ${['nameLocation', 'lastYear', 'degree', 'major'].map((key) => `<td><input id="edu${index}_${key}" name="edu_${index}_${key}"></td>`).join('')}
    </tr>`).join('');
}

function fieldHtml(id: string, name: string, type: string): string {
  if (type === 'textarea') return `<textarea id="${safeAttr(id)}" name="${safeAttr(name)}" rows="2"></textarea>`;
  if (type === 'select') return `<select id="${safeAttr(id)}" name="${safeAttr(name)}"><option value="">Select</option><option>Yes</option><option>No</option></select>`;
  return `<input id="${safeAttr(id)}" name="${safeAttr(name)}" type="${safeAttr(type)}">`;
}

function refreshOutlet(root: HTMLElement): void {
  const selectedCode = root.querySelector<HTMLInputElement>('input[name="outletCode"]:checked')?.value || DEFAULT_OUTLET.code;
  const selected = OUTLETS.find((outlet) => outlet.code === selectedCode) || DEFAULT_OUTLET;
  const employmentType = root.querySelector<HTMLSelectElement>('#employmentType')?.value || '';
  const workTime = employmentType === 'Full-Time' ? selected.fullTimeHours : selected.partTimeHours;
  root.querySelector<HTMLInputElement>('#outletName')!.value = selected.name;
  root.querySelector<HTMLInputElement>('#locationWorkTime')!.value = workTime;
  root.querySelectorAll<HTMLElement>('[data-outlet-card]').forEach((card) => {
    card.classList.toggle('selected', card.dataset.outletCard === selected.code);
  });
  const position = root.querySelector<HTMLSelectElement>('#position')!;
  const current = position.value;
  position.innerHTML = `<option value="">Select</option>${selected.positions.map((item) => `<option>${safe(item)}</option>`).join('')}`;
  if (selected.positions.includes(current)) position.value = current;
  root.querySelector<HTMLElement>('#locationSummary')!.innerHTML = `
    <b>${safe(selected.name)}</b>
    <span>${safe(selected.address)}</span>
    <span>${safe(workTime)}</span>`;
  root.querySelector<HTMLElement>('#hourlyCell')!.classList.toggle('highlight', employmentType === 'Part-Time');
  root.querySelector<HTMLElement>('#salaryCell')!.classList.toggle('highlight', employmentType === 'Full-Time');
}

function syncMobileWork(root: HTMLElement): void {
  WORK_LABELS.forEach(([key]) => {
    [0, 1, 2].forEach((index) => {
      const desktop = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#work${index}_${key}`);
      const mobile = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#mwork${index}_${key}`);
      desktop?.addEventListener('input', () => { if (mobile) mobile.value = desktop.value; });
      desktop?.addEventListener('change', () => { if (mobile) mobile.value = desktop.value; });
      mobile?.addEventListener('input', () => { if (desktop) desktop.value = mobile.value; });
      mobile?.addEventListener('change', () => { if (desktop) desktop.value = mobile.value; });
    });
  });
}

function collectWork(root: HTMLElement): Record<string, string>[] {
  return [0, 1, 2].map((index) => {
    const item: Record<string, string> = {};
    WORK_LABELS.forEach(([key]) => {
      item[key] = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#work${index}_${key}`)?.value.trim() || '';
    });
    return item;
  });
}

function collectEducation(root: HTMLElement): Record<string, string>[] {
  return EDUCATION_TYPES.map((type, index) => ({
    type,
    nameLocation: root.querySelector<HTMLInputElement>(`#edu${index}_nameLocation`)?.value.trim() || '',
    lastYear: root.querySelector<HTMLInputElement>(`#edu${index}_lastYear`)?.value.trim() || '',
    degree: root.querySelector<HTMLInputElement>(`#edu${index}_degree`)?.value.trim() || '',
    major: root.querySelector<HTMLInputElement>(`#edu${index}_major`)?.value.trim() || '',
  }));
}

function showResult(root: HTMLElement, type: 'success' | 'error', message: string): void {
  const result = root.querySelector<HTMLElement>('#result')!;
  result.className = `result ${type}`;
  result.innerHTML = message;
  result.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function clearResult(root: HTMLElement): void {
  const result = root.querySelector<HTMLElement>('#result')!;
  result.className = 'result';
  result.textContent = '';
}

function today(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function safe(value: unknown): string { return String(value || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]!)); }
function safeAttr(value: unknown): string { return safe(value); }
